/* ===========================================================
   shared/diff.js — what changed between two versions

   For the side-by-side Compare view. Blocks are lined up with a
   common-subsequence match; a removed block next to an added block of
   the same kind is treated as "changed", and inside it the words that
   changed are marked. Marked nodes carry `__d: 'del' | 'ins'`, which
   the static renderer (shared/render.js) turns into highlights.

   diffDocs(a, b) → { rows: [{ kind, left, right }], stats }
     kind: 'same' | 'del' | 'ins' | 'mod'
   =========================================================== */

import { canon } from './blocks.js';
import { matchPairs } from './seqdiff.js';
import { inlineTokens, fromTokens } from './merge3.js';

const PROSE = new Set(['paragraph', 'heading']);
const CODE = new Set(['codeBlock', 'sandboxCode']);
const CONTAINERS = new Set(['bulletList', 'orderedList', 'taskList', 'listItem', 'taskItem', 'blockquote', 'table',
  'tableRow', 'tableCell', 'tableHeader', 'sandbox']);

export function diffDocs(a, b) {
  const rows = diffBlocks(a?.content || [], b?.content || []);
  const stats = { added: 0, removed: 0, changed: 0 };
  for (const r of rows) {
    if (r.kind === 'ins') stats.added++;
    else if (r.kind === 'del') stats.removed++;
    else if (r.kind === 'mod') stats.changed++;
  }
  return { rows, stats, same: !stats.added && !stats.removed && !stats.changed };
}

/** Line up two block lists into rows. */
export function diffBlocks(left, right) {
  const kl = left.map(canon), kr = right.map(canon);
  const pairs = matchPairs(kl, kr);
  const rows = [];
  let i = 0, j = 0;
  const flush = (iEnd, jEnd) => {
    const dels = left.slice(i, iEnd), inss = right.slice(j, jEnd);
    const n = Math.max(dels.length, inss.length);
    for (let k = 0; k < n; k++) {
      const l = dels[k], r = inss[k];
      if (l && r && l.type === r.type) rows.push({ kind: 'mod', ...modify(l, r) });
      else {
        if (l) rows.push({ kind: 'del', left: mark(l, 'del'), right: null });
        if (r) rows.push({ kind: 'ins', left: null, right: mark(r, 'ins') });
      }
    }
    i = iEnd; j = jEnd;
  };
  for (const [pi, pj] of pairs) {
    flush(pi, pj);
    rows.push({ kind: 'same', left: left[pi], right: right[pj] });
    i = pi + 1; j = pj + 1;
  }
  flush(left.length, right.length);
  return rows;
}

/** A whole block marked as removed or added (and everything inside it). */
function mark(node, d) {
  const out = { ...node, __d: d };
  if (node.content) out.content = node.content.map(k => mark(k, d));
  return out;
}

/** Two versions of the same kind of block → both sides with the changed parts marked. */
function modify(l, r) {
  if (PROSE.has(l.type)) {
    const [lc, rc] = wordDiff(l.content || [], r.content || []);
    return { left: { ...l, content: lc, __d: 'mod' }, right: { ...r, content: rc, __d: 'mod' } };
  }
  if (CODE.has(l.type)) {
    const [lc, rc] = lineDiff(l.content || [], r.content || []);
    return { left: { ...l, content: lc, __d: 'mod' }, right: { ...r, content: rc, __d: 'mod' } };
  }
  if (CONTAINERS.has(l.type)) {
    const rows = diffBlocks(l.content || [], r.content || []);
    return {
      left: { ...l, __d: 'mod', content: rows.map(x => x.left).filter(Boolean) },
      right: { ...r, __d: 'mod', content: rows.map(x => x.right).filter(Boolean) },
    };
  }
  return { left: mark(l, 'del'), right: mark(r, 'ins') };
}

function wordDiff(lc, rc) {
  const lt = inlineTokens(lc), rt = inlineTokens(rc);
  const pairs = matchPairs(lt.map(t => t.k), rt.map(t => t.k));
  const keepL = new Set(pairs.map(p => p[0])), keepR = new Set(pairs.map(p => p[1]));
  const tag = (tokens, keep, d) => tokens.map((t, i) => {
    if (keep.has(i)) return t;
    if (t.node) return { node: { ...t.node, __d: d } };
    return { ...t, d };
  });
  return [fromTokens(tag(lt, keepL, 'del')), fromTokens(tag(rt, keepR, 'ins'))];
}

function lineDiff(lc, rc) {
  const lines = content => ((content || []).map(n => n.text || '').join('').match(/[^\n]*\n|[^\n]+$/g) || []);
  const ll = lines(lc), rl = lines(rc);
  const pairs = matchPairs(ll, rl);
  const keepL = new Set(pairs.map(p => p[0])), keepR = new Set(pairs.map(p => p[1]));
  const build = (arr, keep, d) => fromTokens(arr.map((text, i) => ({ text, marks: undefined, d: keep.has(i) ? undefined : d })));
  return [build(ll, keepL, 'del'), build(rl, keepR, 'ins')];
}
